import { FloatingLabel, Form, FormLabel } from "react-bootstrap";
import styles from "./FiltersMenu.module.css";
import { ChangeEvent, useEffect, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import Select, { MultiValue } from "react-select";
import { filterStyles, selectTheme } from "../../../../../utils/selectUtils/selectUtils";
import { Api } from "../../../../../services/Api";

export interface IFilters {
    minPublicationDateTime?: string | null;
    maxPublicationDateTime?: string | null;
    minPreparationTime?: number | null;
    maxPreparationTime?: number | null;
    isPublic?: boolean | null;
    isFromExplore?: boolean | null;
    languageId?: string | null;
    tags?: string | null;
    searchQuery?: string | null;
}

interface IFiltersNotNull {
    minPublicationDateTime?: string | undefined;
    maxPublicationDateTime?: string | undefined;
    minPreparationTime?: number | undefined;
    maxPreparationTime?: number | undefined;
    isPublic?: boolean | undefined;
    isFromExplore?: boolean | undefined;
    languageId?: string | undefined;
    tags?: string | undefined;
    searchQuery?: string | undefined;
}

interface IFiltersMenu {
    visible: boolean;
    isExploreMode: boolean;
    selectedFilters(filters: IFilters): void;
}

type ValueLabelOption = { value: number | string; label: string };

const FiltersMenu: React.FC<IFiltersMenu> = (props) => {
    const { t } = useTranslation();
    const [languages, setLangauges] = useState<ValueLabelOption[]>([]);
    const [isLoadingLanguages, setIsLoadingLanguages] = useState(true);
    const [selectedLanguage, setSelectedLanguage] = useState<ValueLabelOption>();
    const [selectedTags, setSelectedTags] = useState<ValueLabelOption[]>([]);
    const [isLoadingAllTags, setIsLoadingAllTags] = useState(true);
    const [tags, setTags] = useState<ValueLabelOption[]>([]);
    const [areFiltersUpdated, setAreFiltersUpdated] = useState(false);
    const [filters, setFilters] = useState<IFiltersNotNull>({
        minPublicationDateTime: undefined,
        maxPublicationDateTime: undefined,
        minPreparationTime: undefined,
        maxPreparationTime: undefined,
        isPublic: undefined,
        isFromExplore: undefined,
        languageId: undefined,
        tags: undefined,
        searchQuery: undefined,
    });

    const handleNumberChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value === "" ? undefined : Number(value) });
        setAreFiltersUpdated(true);
    };

    const handleRadioChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value === "" ? undefined : value === "true" ? true : false });
        setAreFiltersUpdated(true);
    };

    const handleLanguageChange = (event: any) => {
        var label = "";
        var value = "";
        if (event) {
            label = event.label;
            value = event.value;
            setSelectedLanguage({ value, label });
        } else {
            setSelectedLanguage(undefined);
        }
        setFilters({ ...filters, languageId: value === "" ? undefined : value });
        setAreFiltersUpdated(true);
    };

    const handleTagsChange = (newValue: MultiValue<ValueLabelOption>) => {
        setSelectedTags(newValue as ValueLabelOption[]);
        setAreFiltersUpdated(true);
    };

    useEffect(() => {
        if (areFiltersUpdated) {
            props.selectedFilters(filters);
        }
    }, [filters, areFiltersUpdated]);

    useEffect(() => {
        const stringTags = selectedTags.map((item) => item.value).join(",");
        setFilters({ ...filters, tags: stringTags === "" ? undefined : stringTags });
    }, [selectedTags]);

    useEffect(() => {
        Api.get("languages/all")
            .then((response) => {
                return response.data;
            })
            .then((result) => {
                setLangauges(
                    result.map((language: any) => ({
                        value: language.id,
                        label: language.name,
                    })),
                );
                setIsLoadingLanguages(false);
            });

        Api.get("tags/all")
            .then((res) => res.data)
            .then((result) => {
                setTags(
                    result.map((tag: any) => ({
                        value: tag.id,
                        label: tag.name,
                    })),
                );
                setIsLoadingAllTags(false);
            });
    }, []);

    return (
        <div className={`${styles.container} ${styles.flexWrap}`} style={{ display: props.visible ? "flex" : "none" }}>
            <div className={styles.filterContainer}>
                <div className={styles.title}>
                    <b>{t("components.feature.search.filters.tags")}</b>
                </div>
                <Form.Group controlId="tags" className="mb-3">
                    <Select
                        className={styles.tagsForm}
                        isSearchable={true}
                        isLoading={isLoadingAllTags}
                        value={selectedTags}
                        isMulti
                        options={tags}
                        onChange={handleTagsChange}
                        styles={filterStyles}
                        theme={selectTheme}
                        placeholder={t("components.feature.search.filters.tags").toLowerCase() + "..."}
                    />
                </Form.Group>
            </div>

            <div className={styles.filterContainer}>
                <div className={styles.title}>
                    <b>{t("components.feature.search.filters.language")}</b>
                </div>
                <Form.Group controlId="languageId" className="mb-3">
                    <Select
                        className={styles.langaugeForm}
                        isSearchable={true}
                        name="language"
                        isLoading={isLoadingLanguages}
                        value={selectedLanguage}
                        options={languages}
                        isClearable={true}
                        placeholder={t("components.feature.search.filters.language").toLowerCase() + "..."}
                        onChange={handleLanguageChange}
                        styles={filterStyles}
                        theme={selectTheme}
                    />
                </Form.Group>
            </div>

            <div className={styles.filterContainer}>
                <div className={styles.title}>
                    <b>{t("components.feature.search.filters.preparationTime")}</b>
                </div>
                <div className={styles.flexWrap}>
                    <Form.Group controlId="preparationTime" className="me-2 mb-3">
                        <Form.Control
                            className={styles.numericForm}
                            type="number"
                            name="minPreparationTime"
                            placeholder={t("components.feature.search.filters.minPreparationTime")}
                            min="0"
                            value={filters.minPreparationTime}
                            onChange={handleNumberChange}
                        />
                    </Form.Group>

                    <Form.Group controlId="preparationTime" className="mb-3">
                        <Form.Control
                            className={styles.numericForm}
                            type="number"
                            name="maxPreparationTime"
                            placeholder={t("components.feature.search.filters.maxPreparationTime")}
                            min="0"
                            value={filters.maxPreparationTime}
                            onChange={handleNumberChange}
                        />
                    </Form.Group>
                </div>
            </div>

            <div className={styles.filterContainer}>
                <div className={styles.title}>
                    <b>{t("components.feature.search.filters.visibility")}</b>
                </div>
                <Form.Group className={styles.flexWrap}>
                    <Form.Check
                        defaultChecked={!props.isExploreMode}
                        disabled={props.isExploreMode}
                        type="radio"
                        name="isPublic"
                        label={t("common.any")}
                        value=""
                        className="me-2 mb-3"
                        onChange={handleRadioChange}
                    />
                    <Form.Check
                        defaultChecked={props.isExploreMode}
                        type="radio"
                        name="isPublic"
                        label={t("common.public")}
                        value="true"
                        className="mb-3 me-2"
                        onChange={handleRadioChange}
                    />
                    <Form.Check
                        disabled={props.isExploreMode}
                        type="radio"
                        name="isPublic"
                        label={t("common.private")}
                        value="false"
                        className="me-2 mb-3"
                        onChange={handleRadioChange}
                    />
                </Form.Group>
            </div>

            <div className={styles.filterContainer}>
                <div className={styles.title}>
                    <b>{t("components.feature.search.filters.origin")}</b>
                </div>
                <Form.Group className={styles.flexWrap}>
                    <Form.Check
                        defaultChecked
                        type="radio"
                        name="isFromExplore"
                        label={t("common.any")}
                        value=""
                        className="me-2 mb-3"
                        onChange={handleRadioChange}
                    />
                    <Form.Check
                        disabled={props.isExploreMode}
                        type="radio"
                        name="isFromExplore"
                        label={t("components.feature.search.filters.mine")}
                        value="false"
                        className="me-2 mb-2"
                        onChange={handleRadioChange}
                    />
                    <Form.Check
                        disabled={props.isExploreMode}
                        type="radio"
                        name="isFromExplore"
                        label={t("components.feature.search.filters.fromExplore")}
                        value="true"
                        className="mb-3"
                        onChange={handleRadioChange}
                    />
                </Form.Group>
            </div>
        </div>
    );
};

export default FiltersMenu;
